import { ApiResponse } from 'apisauce';
import api from '../utils/api/api.service';
import errorHelper from '../utils/api/error-helper.service';
import LeadMapper from '../../mappers/lead.mapper';
import {
  LeadActivityModel,
  LeadCommentModel,
  LeadConfigModel,
  LeadModel,
  LeadSearchQueryModel
} from '../../models/lead.model';
import {
  LeadActivityUiModel,
  LeadCommentUiModel,
  LeadUiModel
} from '../../ui-models/lead.ui-model';
import { ErrorInfo } from '../../interfaces/error-info.interface';
import { ResponseModel } from '../../interfaces/response-model.interface';

/**
 * Leads: the pre-sale funnel across furnishing, acquisition and reservations.
 *
 * In the platform layer rather than inside LeadModule because the lead config
 * is read by more than the leads screens — any form that offers a resolution or
 * a status needs it.
 */
const MODULE_NAME = 'lead';

/**
 * Builds the module's ErrorInfo from a failed apisauce response.
 *
 * Typed loosely on purpose: `ApiResponse<T, E>` carries `data` as the success
 * shape, so an error body has to be read through a cast whatever we do. Keeping
 * that cast in one place is better than repeating it at seventeen call sites.
 */
const fail = (response: unknown, fn: unknown) => {
  const res = response as { status?: number; data?: { errorCode?: string }; problem?: unknown };
  return errorHelper(
    Number(res.status),
    res.data && res.data.errorCode ? String(res.data.errorCode || '') : String(res.problem || ''),
    MODULE_NAME,
    (fn as { displayName?: string }).displayName as string
  );
};

/**
 * The enums and policy tables every lead form is built from.
 *
 * Fetched rather than hardcoded on purpose: a local copy drifts from the
 * service that validates it, and the drift only appears as a 400 the operator
 * cannot act on.
 */
export function getLeadConfig(): Promise<LeadConfigModel | ErrorInfo> {
  return api
    .getLeadConfig()
    .then((response: ApiResponse<ResponseModel<LeadConfigModel>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) return response.data.data;
      return fail(response, api.getLeadConfig);
    });
}

/**
 * Creates a lead.
 *
 * Returns `{ id, shortId, created }`. **`created: false` is a success, not a
 * failure** — the service deduplicates against an open lead for the same
 * person in the same vertical and hands back the existing one. A caller that
 * treats it as a new record will report a fresh lead that is actually a
 * second enquiry on an old one.
 */
export function createLead(
  lead: Record<string, unknown>
): Promise<{ id: string; shortId?: number; created: boolean } | ErrorInfo> {
  return api.createLead(lead as Partial<LeadModel>).then((response) => {
    if (response.ok && response.data && response.data.data) {
      const body = response.data.data as { id: string; shortId?: number; created?: boolean };
      return { id: body.id, shortId: body.shortId, created: body.created !== false };
    }
    return fail(response, api.createLead);
  });
}

export function searchLeads(
  query: LeadSearchQueryModel,
  limit?: number,
  skip?: number
): Promise<{ data: LeadUiModel[]; total: number } | ErrorInfo> {
  return api
    .searchLeads(query, limit, skip)
    .then((response: ApiResponse<ResponseModel<LeadModel[]>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return {
          data: LeadMapper.mapToUIList(response.data.data as LeadModel[]),
          // The service returns `documents_counts`; the table pages on it.
          total: (response.data as unknown as { documents_counts?: number }).documents_counts ?? 0
        };
      }
      return fail(response, api.searchLeads);
    });
}

export function getLeadById(leadId: string): Promise<LeadUiModel | ErrorInfo> {
  return api
    .getLeadById(leadId)
    .then((response: ApiResponse<ResponseModel<LeadModel>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return LeadMapper.mapToUI(response.data.data as LeadModel);
      }
      return fail(response, api.getLeadById);
    });
}

export function getLeadTimeline(leadId: string): Promise<LeadActivityUiModel[] | ErrorInfo> {
  return api
    .getLeadTimeline(leadId)
    .then((response: ApiResponse<ResponseModel<LeadActivityModel[]>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return LeadMapper.mapActivityToUIList(response.data.data as LeadActivityModel[]);
      }
      return fail(response, api.getLeadTimeline);
    });
}

export function getLeadComments(leadId: string): Promise<LeadCommentUiModel[] | ErrorInfo> {
  return api
    .getLeadComments(leadId)
    .then((response: ApiResponse<ResponseModel<LeadCommentModel[]>, ErrorInfo>) => {
      if (response.ok && response.data && response.data.data) {
        return LeadMapper.mapCommentToUIList(response.data.data as LeadCommentModel[]);
      }
      return fail(response, api.getLeadComments);
    });
}

export function addLeadComment(leadId: string, text: string): Promise<string | ErrorInfo> {
  return api.createLeadComment(leadId, text).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return (response.data.data as { id: string }).id;
    }
    return fail(response, api.createLeadComment);
  });
}

/**
 * Moves a lead through its lifecycle.
 *
 * `payload` is passed through rather than typed per resolution because the
 * required reason depends on the resolution, and that rule is published by the
 * config endpoint. Encoding it a second time here is how the two drift.
 */
export function changeLeadStatus(
  leadId: string,
  payload: Record<string, unknown>
): Promise<LeadUiModel | ErrorInfo> {
  return api.changeLeadStatus(leadId, payload).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return LeadMapper.mapToUI(response.data.data as LeadModel);
    }
    return fail(response, api.changeLeadStatus);
  });
}

export function recordContactAttempt(
  leadId: string,
  note?: string
): Promise<LeadUiModel | ErrorInfo> {
  return api.recordContactAttempt(leadId, { note }).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return LeadMapper.mapToUI(response.data.data as LeadModel);
    }
    return fail(response, api.recordContactAttempt);
  });
}

export function assignLead(leadId: string, assignedTo: string): Promise<LeadUiModel | ErrorInfo> {
  return api.assignLead(leadId, assignedTo).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return LeadMapper.mapToUI(response.data.data as LeadModel);
    }
    return fail(response, api.assignLead);
  });
}

export function markLeadDuplicate(
  leadId: string,
  duplicateOfLeadId: string
): Promise<LeadUiModel | ErrorInfo> {
  return api.markLeadDuplicate(leadId, duplicateOfLeadId).then((response) => {
    if (response.ok && response.data && response.data.data) {
      return LeadMapper.mapToUI(response.data.data as LeadModel);
    }
    return fail(response, api.markLeadDuplicate);
  });
}
